import { set } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import type { TypeToModel } from "@shared/models/type-to-model"
import { task, timeout } from "ember-concurrency"
import { forEach, isNil } from "lodash-es"
import type Finder from "./finder"
import type InternalEmberStore from "./internal-ember-store"
import type SocketService from "./socket"

/** How often we loop through our store checking for stale documents. */
const CHECK_STALENESS_EVERY = 1000 * 60 * 10 // 10 minutes

/** Have a lower bound of how many times we can call the fn. */
const CHECK_STALENESS_NO_MORE_THAN = 1000 * 60 * 2 // 2 minutes

/** At what point we ask the server to see if it has a new revision. */
const DOCUMENT_STALE_AFTER = 1000 * 60 * 30 // 30 minutes

interface CheckStalenessOptions {
  ignoreTimeSinceFetch: boolean
}

export default class DocumentStalenessMonitor extends Service {
  @service("internal-ember-store") declare internalEmberStore: InternalEmberStore
  @service declare finder: Finder
  @service declare socket: SocketService

  private hasStarted = false

  start(): void {
    if (this.hasStarted) return
    this.hasStarted = true
    this.scheduleNextCheck()
  }

  check = task(
    { keepLatest: true },
    async (opts: CheckStalenessOptions = { ignoreTimeSinceFetch: false }) => {
      const startCheck = performance.now()
      let docsIteratedOver = 0
      let docsThatWereChecked = 0
      let docsThatWereStale = 0

      // Initially, I don't want to check card-stack-summary as there can be thousand+ (100s per class * n classes)
      // Instead, I'd like to just check the big ones -- course, planbook card-stack, group, user, etc
      // As we see how this works, we can take out this limitation
      const typesToCheck: Array<keyof TypeToModel> = [
        "card-stack",
        "class-website",
        "course",
        "fiscal-group",
        "group",
        "planbook",
        "rotation-calendar",
        "user",
      ]
      const now = new Date()
      forEach(typesToCheck, (type) => {
        forEach(this.internalEmberStore.documentsForType(type), (doc, id) => {
          docsIteratedOver++
          const timeSinceFetch = doc?.meta?.requestedAt
            ? now.valueOf() - dateFns.parse(doc?.meta?.requestedAt)?.valueOf()
            : null
          if (
            !isNil(timeSinceFetch) &&
            !isNaN(timeSinceFetch) &&
            (opts.ignoreTimeSinceFetch === true || timeSinceFetch > DOCUMENT_STALE_AFTER)
          ) {
            docsThatWereChecked++
            this.socket.checkStaleness(type, id)?.then((result) => {
              const _revision = result?._revision
              if (_revision === null || _revision === undefined) return

              const ourRevision = doc?.attributes?._revision
              if (_revision > ourRevision) {
                console.log(
                  `We have a stale document: ${type}:${id}. We have ${ourRevision}. We need ${_revision}.`
                )
                docsThatWereStale++
                this.finder.find(type, id)
              } else if (_revision === ourRevision) {
                set(doc, "meta.requestedAt", now.toISOString())
              }
            })
          }
        })
      })
      const timeTaken = performance.now() - startCheck
      console.log(`Staleness Check Complete.`)
      setTimeout(() => {
        console.log(
          `Iteration took: ${timeTaken}ms. Documents Scanned: ${docsIteratedOver}. Documents Checked: ${docsThatWereChecked}. Stale Documents: ${docsThatWereStale}`
        )
      }, 3000)
      await timeout(CHECK_STALENESS_NO_MORE_THAN)
    }
  )

  private scheduleNextCheck(): void {
    setTimeout(() => {
      if (this.isDestroyed || this.isDestroying) return

      // We don't need to check staleness if the browser doesn't have focus.
      const hasFocus = document.hasFocus() && document.visibilityState !== "hidden"
      if (hasFocus) this.check.perform()
      this.scheduleNextCheck()
    }, CHECK_STALENESS_EVERY)
  }
}

declare module "@ember/service" {
  interface Registry {
    "document-staleness-monitor": DocumentStalenessMonitor
  }
}
